import { Link, createFileRoute, notFound } from "@tanstack/react-router";
import { ExpandableIntro } from "@/components/expandable-intro";
import { Check, X } from "lucide-react";

import { ArticleBody, ArticleToc } from "@/components/article-body";
import { AuthorByline } from "@/components/author-byline";
import { CasinoList } from "@/components/casino-list";
import { QualityGuarantee } from "@/components/quality-guarantee";
import { MethodComparison } from "@/components/method-comparison";
import { PaymentQuickAnswer } from "@/components/payment-quick-answer";
import { getPaymentFaqs } from "@/content/payment-additions";
import { DepositSpeedInfographic, MethodDistributionInfographic, MethodFlowInfographic, PayoutSpeedInfographic } from "@/components/infographics";
import { KeyTakeaways } from "@/components/key-takeaways";
import { MethodFactSheet } from "@/components/method-fact-sheet";
import { MethodTestReport } from "@/components/method-test-report";
import { EditorialTeam } from "@/components/editorial-team";
import { EeatTrustBlocks, HowWeTested } from "@/components/eeat-blocks";
import { getMethodFacts } from "@/lib/methodfacts";
import { LinkBudget, linkifyText } from "@/lib/internal-links";
import { methodTestStats } from "@/lib/testdata";
import { getAuthor } from "@/lib/authors";
import { getMethodArticle } from "@/content";
import { METHOD_IMAGE } from "@/content/images";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { formatDate, formatEuro } from "@/lib/format";
import { MethodLogo } from "@/components/method-logo";
import { getMethodBySlug, getPublicCasinos } from "@/lib/queries";
import { SITE } from "@/lib/site";
import { notFoundStatus } from "@/lib/http-status";
import { methodOgMeta } from "@/lib/og";

export const Route = createFileRoute("/betaalmethoden/$slug")({
  loader: async ({ params }) => {
    const method = getMethodBySlug(params.slug);
    if (!method) {
      await notFoundStatus();
      throw notFound();
    }
    return { method };
  },
  head: ({ loaderData }) => {
    const method = loaderData?.method;
    if (!method) return {};
    const methodArticle = getMethodArticle(method.slug);
    const articleAuthor = methodArticle ? getAuthor(methodArticle.authorSlug) : null;
    const articleReviewer = methodArticle ? getAuthor(methodArticle.reviewerSlug) : null;
    const methodCasinos = getPublicCasinos().filter((casino) =>
      casino.links.some((link) => link.method?.slug === method.slug),
    );
    return {
      meta: [
        { title: method.seo_title ?? `${method.name} casino's | CasinoBetalingen.com` },
        { name: "description", content: method.meta_description ?? method.short_description ?? "" },
        { property: "og:title", content: method.seo_title ?? `${method.name} casino's` },
        { property: "og:description", content: method.meta_description ?? method.short_description ?? "" },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        ...methodOgMeta(method.slug),
        { property: "og:url", content: `${SITE.url}/betaalmethoden/${method.slug}/` },
      ],
      links: [{ rel: "canonical", href: `${SITE.url}/betaalmethoden/${method.slug}/` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: method.seo_title ?? `${method.name} casino's`,
            description: method.meta_description ?? method.short_description ?? "",
            inLanguage: "nl-NL",
            dateModified: method.last_verified_at,
            mainEntityOfPage: `${SITE.url}/betaalmethoden/${method.slug}/`,
            author: articleAuthor
              ? { "@type": "Person", name: articleAuthor.name, jobTitle: articleAuthor.role }
              : { "@type": "Organization", name: SITE.name },
            reviewedBy: articleReviewer
              ? { "@type": "Person", name: articleReviewer.name, jobTitle: articleReviewer.role }
              : undefined,
            publisher: { "@id": `${SITE.url}/#organization` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `Beste casino's met ${method.name}`,
            numberOfItems: methodCasinos.length,
            itemListOrder: "https://schema.org/ItemListOrderAscending",
            itemListElement: methodCasinos.map((casino, index) => ({
              "@type": "ListItem",
              position: index + 1,
              item: {
                "@type": "Organization",
                name: casino.name,
                description: `${method.name} storten vanaf ${formatEuro(
                  casino.links.find((link) => link.method?.slug === method.slug)?.deposit_minimum,
                )}`,
              },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: getPaymentFaqs(method.slug, method.faq_items).map((item) => ({
              "@type": "Question",
              name: item.q,
              acceptedAnswer: { "@type": "Answer", text: item.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
              { "@type": "ListItem", position: 2, name: "Betaalmethoden", item: `${SITE.url}/betaalmethoden/` },
              { "@type": "ListItem", position: 3, name: method.name },
            ],
          }),
        },
      ],
    };
  },
  component: MethodDetailPage,
});

function MethodDetailPage() {
  const { method } = Route.useLoaderData();
  const supporting = getPublicCasinos().filter((c) =>
    c.links.some((l) => l.method?.slug === method.slug),
  );
  /** Geen aanbieder ondersteunt de methode: toon de vaste, sitewide casinolijst. */
  const casinos = supporting.length > 0 ? supporting : getPublicCasinos();
  const article = getMethodArticle(method.slug);
  const image = METHOD_IMAGE[method.slug];
  const facts = getMethodFacts(method.slug);
  const stats = methodTestStats(method.slug);
  /** Eén gedeeld budget: interne links verspreid over intro, artikel én FAQ. */
  const linkBudget = new LinkBudget(`/betaalmethoden/${method.slug}`);

  const takeaways = [
    `${method.name} schrijft in onze ${stats.tests} tests mediaan binnen ${stats.medianDepositSeconds} seconden bij (${stats.fastestDepositSeconds}–${stats.slowestDepositSeconds} sec.).`,
    !stats.withdrawalSupportedInTests
      ? `Opnemen kan niet met ${method.name}: winst gaat naar je bankrekening of wallet.`
      : method.withdrawal_supported
        ? `Uitbetalen duurde mediaan ${stats.medianWithdrawalHours} uur, met ${stats.fastestWithdrawalHours} uur als snelste meting.`
        : `Opnemen met ${method.name} zelf kan niet; de terugbetaling naar je bankrekening stond er mediaan ${stats.medianWithdrawalHours} uur op.`,
    `In ${stats.kycRate}% van de tests vroeg de aanbieder om aanvullende identificatie voordat de opname werd vrijgegeven.`,
    `Kosten voor de speler: ${facts?.playerCost ?? method.typical_fees ?? "onbekend"}.`,
    `${supporting.length} van de ${getPublicCasinos().length} vergeleken aanbieders accepteren ${method.name}.`,
    `Stortingen lopen van ${formatEuro(method.min_deposit_general)} tot ${formatEuro(method.max_deposit_general)} per transactie.`,
  ];

  const specs: { label: string; value: string }[] = [
    { label: "Stortsnelheid", value: method.typical_deposit_speed ?? "Onbekend" },
    {
      label: "Opnemen",
      value: method.withdrawal_supported
        ? (method.typical_withdrawal_speed ?? "Mogelijk")
        : "Niet beschikbaar",
    },
    { label: "Minimale storting", value: formatEuro(method.min_deposit_general) },
    { label: "Maximale storting", value: formatEuro(method.max_deposit_general) },
    { label: "Kosten", value: method.typical_fees ?? "Onbekend" },
    { label: "Mobiel", value: method.mobile_support ? "Ondersteund" : "Niet ondersteund" },
  ];

  return (
    <main className="mx-auto max-w-7xl px-4 py-4 sm:px-6 sm:py-12">
      <nav aria-label="Broodkruimels" className="mb-2 text-xs text-muted-foreground sm:mb-6 sm:text-sm">
        <Link to="/" className="hover:text-foreground">Home</Link>
        <span className="mx-2">/</span>
        <Link to="/betaalmethoden/" className="hover:text-foreground">Betaalmethoden</Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">{method.name}</span>
      </nav>

      <div className="flex items-center gap-3 sm:flex-wrap sm:gap-4">
        <span className="hidden sm:inline-flex"><MethodLogo slug={method.slug} name={method.name} size="lg" /></span>
        <h1 className="text-2xl font-bold leading-tight tracking-tight text-foreground sm:text-4xl">
          {method.slug === "klarna" ? method.seo_title : method.slug === "telefoonrekening" ? "Online casino betalen met telefoonrekening" : method.deposit_supported ? method.slug === "revolut" ? "Beste Revolut casino's in Nederland" : `Beste ${method.name} casino's` : `${method.name} casino's en Riverty: achteraf betalen in het casino`}
        </h1>
        <Badge variant="secondary" className="hidden capitalize sm:inline-flex">{method.category}</Badge>
      </div>
      <p className="mt-2 hidden text-xs text-muted-foreground sm:block">
        Laatst gecontroleerd: {formatDate(method.last_verified_at)} · Gegevens kunnen per aanbieder afwijken.
      </p>

      {article ? (
        <div className="mt-2 max-w-3xl sm:mt-6">
          <AuthorByline
            authorSlug={article.authorSlug}
            reviewerSlug={article.reviewerSlug}
            updatedAt={article.updatedAt}
            readingMinutes={article.readingMinutes}
          />
        </div>
      ) : null}
      <ExpandableIntro>{method.intro}</ExpandableIntro>


      <div className="mt-4 grid gap-10 sm:mt-10 lg:grid-cols-[2fr_1fr]">
        <div className="min-w-0">
          <section aria-labelledby="casinos">
            <h2 id="casinos" className="text-xl font-semibold text-foreground">
              {supporting.length > 0 ? `Beste ${method.name} casino's getest in november 2026` : `${method.name} casino's: de beste alternatieven getest in november 2026`}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {supporting.length > 0
                ? `Alle ${casinos.length} aanbieders in onze vergelijking ondersteunen ${method.name}.`
                : `${method.name} staat bij geen van deze aanbieders in de kassa; hieronder onze volledige vergelijking met directe betaalmethoden.`}{" "}
              Volgorde op basis van onze eigen betaaltests.
            </p>
            <div className="mt-4">
              <CasinoList
                casinos={casinos}
                {...(supporting.length > 0 ? { methodSlug: method.slug, methodName: method.name } : {})}
              />
            </div>
            {image ? (
              <figure className="mt-6 overflow-hidden rounded-xl border border-border">
                <img
                  src={image.src}
                  alt={image.alt}
                  width={1280}
                  height={720}
                  loading="lazy" decoding="async" className="aspect-[21/9] w-full object-cover"
                />
                <figcaption className="border-t border-border bg-secondary/50 px-4 py-2 text-xs text-muted-foreground">
                  Beeld: redactie {SITE.name}
                </figcaption>
              </figure>
            ) : null}
          </section>

          <PaymentQuickAnswer method={method} />

          <div className="mt-10">
            <HowWeTested
              subject={method.name}
              providers={10}
              period="november 2026"
            />
          </div>

          <div className="mt-8"><KeyTakeaways items={takeaways} /></div>
          <section className="mt-10" aria-labelledby="specs">
            <h2 id="specs" className="text-xl font-semibold text-foreground">Kenmerken van {method.name}</h2>
            <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {specs.map((spec) => (
                <div key={spec.label} className="rounded-lg border border-border bg-card p-4">
                  <dt className="text-xs text-muted-foreground">{spec.label}</dt>
                  <dd className="mt-1 break-words text-sm font-medium text-foreground">{spec.value}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="mt-10" aria-labelledby="uitleg">
            <h2 id="uitleg" className="text-xl font-semibold text-foreground">
              Hoe werkt {method.name}?
            </h2>
            <p className="prose-nl mt-3 text-muted-foreground">
              {linkifyText(method.full_description ?? "", linkBudget, "intro")}
            </p>
            {method.verification_notes ? (
              <p className="mt-3 rounded-lg border border-border bg-secondary/50 p-4 text-sm text-muted-foreground">
                <strong className="text-foreground">Let op:</strong> {method.verification_notes}
              </p>
            ) : null}
          </section>

          {article ? (
            <section className="mt-12" aria-label={`Alles over ${method.name}`}>
              <ArticleBody
                blocks={article.blocks}
                currentPath={`/betaalmethoden/${method.slug}`}
                budget={linkBudget}
              />
            </section>
          ) : null}

          <div className="mt-12 grid gap-6">
            <MethodFlowInfographic methodSlug={method.slug} methodName={method.name} />
            <MethodDistributionInfographic methodSlug={method.slug} methodName={method.name} />
          </div>

          {facts ? (
            <div className="mt-12">
              <MethodFactSheet facts={facts} methodName={method.name} />
            </div>
          ) : null}

          <div className="mt-14">
            <MethodTestReport
              methodSlug={method.slug}
              methodName={method.name}
              payoutViaBank={!method.withdrawal_supported}
            />
          </div>

          {article ? (
            <div className="mt-14">
              <EditorialTeam
                authorSlug={article.authorSlug}
                reviewerSlug={article.reviewerSlug}
                updatedAt={article.updatedAt}
                methodName={method.name}
              />
            </div>
          ) : null}

          <div className="mt-14 grid gap-6">
            <DepositSpeedInfographic highlightSlug={method.slug} />
            <PayoutSpeedInfographic highlightSlug={method.slug} />
          </div>

          <div className="mt-14">
            <MethodComparison methodSlug={method.slug} />
          </div>

          <section className="mt-10" aria-labelledby="faq">
            <h2 id="faq" className="text-xl font-semibold text-foreground">
              Veelgestelde vragen over {method.name}
            </h2>
            <Accordion type="single" collapsible className="mt-4">
              {getPaymentFaqs(method.slug, method.faq_items).map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left text-sm font-medium">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground">
                    {linkifyText(item.a, linkBudget, `faq-${i}`)}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          {article ? (
            <div className="mt-14">
              <EeatTrustBlocks
                authorSlug={article.authorSlug}
                reviewerSlug={article.reviewerSlug}
                updatedAt={article.updatedAt}
              />
            </div>
          ) : null}
        </div>

        <aside className="min-w-0 space-y-6 lg:sticky lg:top-32 lg:self-start">
          {article ? <ArticleToc blocks={article.blocks} /> : null}
          <div className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold text-foreground">Voordelen</h2>
            <ul className="mt-3 space-y-2">
              {method.pros.map((pro) => (
                <li key={pro} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" aria-hidden />
                  {pro}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <h2 className="text-sm font-semibold text-foreground">Nadelen</h2>
            <ul className="mt-3 space-y-2">
              {method.cons.map((con) => (
                <li key={con} className="flex items-start gap-2 text-sm text-muted-foreground">
                  <X className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
                  {con}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border border-border bg-secondary/50 p-5 text-xs leading-relaxed text-muted-foreground">
            {SITE.disclaimer}
          </div>
        </aside>
      </div>
      <div className="mt-14">
        <QualityGuarantee />
      </div>
    </main>
  );
}
